import { useCallback, useEffect, useRef, useState } from "react";
import { Favorite, type FavoriteKind } from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";

// Shared program/plan favorites store (HR-S3d) — the ⭐ state behind the
// Library browser's favorites tab and per-card star. Server contract:
// GET/POST /api/me/favorites + DELETE /api/me/favorites/:kind/:itemId
// (additive FAVORITE#<kind>#<itemId> rows; the exercise BOOKMARK# rows
// and their mobile store are untouched). Toggle is optimistic — flip the
// set, fire the write, revert on failure and hand the error back so the
// host renders its own message. apiFetch via ref, house convention.

export const favoriteKey = (kind: FavoriteKind, itemId: string): string =>
  `${kind}/${itemId}`;

export type FavoritesStatus = "loading" | "loaded" | "error";

export function useFavorites(opts: {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
}) {
  const [keys, setKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [status, setStatus] = useState<FavoritesStatus>("loading");
  // Key with a write in flight — double-tap guard (same contract as the
  // browser's followingKey): the second tap is dropped, the host disables
  // the star while pending.
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const apiRef = useRef(opts.apiFetch);
  const writeRef = useRef(opts.durableWrite);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    writeRef.current = opts.durableWrite;
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const raw = await apiRef.current<unknown>("/api/me/favorites");
        if (cancelled) return;
        const arr =
          raw && typeof raw === "object" && "items" in raw
            ? (raw as { items: unknown }).items
            : [];
        const next = new Set<string>();
        if (Array.isArray(arr)) {
          for (const item of arr) {
            const parsed = Favorite.safeParse(item);
            if (parsed.success)
              next.add(favoriteKey(parsed.data.kind, parsed.data.itemId));
          }
        }
        setKeys(next);
        setStatus("loaded");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isFavorite = useCallback(
    (kind: FavoriteKind, itemId: string): boolean =>
      keys.has(favoriteKey(kind, itemId)),
    [keys],
  );

  /** Optimistic star/un-star. Resolves null on success, the Error on
   *  failure (state already reverted). */
  const toggleFavorite = useCallback(
    async (kind: FavoriteKind, itemId: string): Promise<Error | null> => {
      const key = favoriteKey(kind, itemId);
      if (pendingKey) return null;
      setPendingKey(key);
      const wasFavorite = keys.has(key);
      setKeys((prev) => {
        const next = new Set(prev);
        if (wasFavorite) next.delete(key);
        else next.add(key);
        return next;
      });
      try {
        const result = wasFavorite
          ? await writeRef.current({
              method: "DELETE",
              path: `/api/me/favorites/${encodeURIComponent(kind)}/${encodeURIComponent(itemId)}`,
            })
          : await writeRef.current({
              method: "POST",
              path: "/api/me/favorites",
              body: { kind, itemId },
            });
        // ⭐ A queued star KEEPS the flip and reports success: it is durable
        // on mobile, so reverting it would be the silent loss DX-2 removes.
        // Only a server verdict reverts.
        const err = writeError(result);
        if (err !== null) {
          setKeys((prev) => {
            const next = new Set(prev);
            if (wasFavorite) next.add(key);
            else next.delete(key);
            return next;
          });
        }
        return err;
      } finally {
        setPendingKey(null);
      }
    },
    [keys, pendingKey],
  );

  return {
    favoriteKeys: keys,
    favoritesStatus: status,
    favoritePendingKey: pendingKey,
    isFavorite,
    toggleFavorite,
  };
}
